import { useState } from 'react';
import SEO from '../components/SEO';
import Nav from '../components/nav';

export default function ProducerBuddyTerms() {
  const [activeTab, setActiveTab] = useState('terms');
  
  const termsAndConditionsSections = [
    {
      title: "1. Acceptance of Terms",
      content: "By accessing or using the Producer Buddy platform, you agree to these Terms and Conditions and our Privacy Policy. If you do not agree with any part of these terms, please discontinue use of the platform."
    },
    {
      title: "2. Use of the Platform",
      content: "You agree to use Producer Buddy only for lawful purposes and in compliance with all applicable laws and regulations.",
      subpoints: [
        "• Engaging in illegal activities.",
        "• Attempting unauthorized access to the platform, data, or systems.",
        "• Harassing, threatening, or exploiting others, including through bullying.",
        "• Uploading or sharing content that infringes intellectual property rights or contains malicious code (e.g., viruses).",
        "• Posting inappropriate or adult-oriented content on public or blog pages meant for general audiences."
      ],
      subtitle: "Prohibited Uses:"
    },
    {
      title: "3. User Accounts",
      content: "To access certain features, users may be required to create an account. You agree to:",
      subpoints: [
        "• Provide accurate and up-to-date registration information.",
        "• Maintain the confidentiality of your credentials.",
        "• Notify us immediately of unauthorized access or suspicious activity."
      ],
      additional: "Producer Buddy reserves the right to suspend or terminate accounts that violate these terms."
    },
    {
      title: "4. Intellectual Property",
      content: "All content on the platform—including text, graphics, logos, software, and images—is the property of Pilomation or its content suppliers and is protected by intellectual property laws. Users may not reproduce, distribute, or create derivative works without explicit written permission."
    },
    {
      title: "5. Content and Data",
      content: "You retain ownership of the content you upload (e.g., scripts, project data). By uploading content, you grant Producer Buddy a non-exclusive, royalty-free, worldwide license to use, display, and modify your content as necessary to provide our services. We take appropriate measures to protect your data in line with our Privacy Policy."
    },
    {
      title: "6. Payments and Subscriptions",
      subcategories: [
        {
          subtitle: "Payment Obligations:",
          subpoints: [
            "• Users must pay applicable fees and taxes for paid features or services.",
            "• Payments must be made through the platform's designated payment gateway."
          ]
        },
        {
          subtitle: "Subscription Features:",
          subpoints: [
            "• Additional storage, premium access, and expert services (like Solution Engineers) may incur extra charges, displayed clearly at the time of purchase."
          ]
        },
        {
          subtitle: "Billing and Refunds:",
          subpoints: [
            "• Timely payment is required to maintain access.",
            "• Refunds and cancellations are subject to the terms shown during the payment process and may not always be available.",
            "• Producer Buddy is not liable for payment network issues, failed transactions, or delays."
          ]
        }
      ]
    },
    {
      title: "7. Terms of Use for Marketing and KPI Data",
      subpoints: [
        "• Key Performance Indicators (KPIs) collected through the platform may be utilized for marketing and promotional purposes by Producer Buddy.",
        "• Any data used for marketing will be anonymized and aggregated to ensure user confidentiality and privacy.",
        "• By using the platform, users consent to the use of KPIs for marketing initiatives, as outlined in the platform's Privacy Policy."
      ]
    },
    {
      title: "8. Limitation of Liability",
      content: "Producer Buddy provides the platform on an \"as-is\" and \"as-available\" basis. We do not guarantee that the platform will be error-free, secure, or continuously available. To the maximum extent permitted by law, Producer Buddy and its affiliates are not liable for any indirect, incidental, or consequential damages, including loss of profits, data, or business opportunities, arising from the use of or inability to use the platform.",
      subtitle: "Additionally:",
      subpoints: [
        "• The platform is not a substitute for professional advice and should not be relied upon for legal, financial, or medical decisions without consulting licensed and qualified experts.",
        "• While Producer Buddy's AI tool provides detailed budgeting estimates, any budget adjustments or negotiations that occur after the release of the project are outside the scope of the tool and are the sole responsibility of the user."
      ]
    },
    {
      title: "9. Modifications to Terms",
      content: "We may revise these terms from time to time with reasonable notice. Continued use of the platform constitutes your acceptance of the revised terms."
    },
    {
      title: "10. Governing Law",
      content: "These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these terms shall be subject to the exclusive jurisdiction of the courts located in your location, India or other countries where the platform is available."
    },
    {
      title: "11. Contact Information",
      content: "For questions or concerns, contact us at:",
      subpoints: [
        "• Email: [support@producersbuddy.com]"
      ]
    },
    {
      title: "12. Entire Agreement",
      content: "These Terms, together with the Privacy Policy, form the entire agreement between you and Producer Buddy."
    }
  ];
  
  const supplementalTerms = [
    {
      title: "A. Terms for Career Page",
      subpoints: [
        {
          subtitle: "1. Acceptance:",
          content: "By using the Career Page, both production houses and job seekers agree to these specific terms."
        },
        {
          subtitle: "2. Responsibilities:",
          subcategories: [
            {
              subtitle: "Production Houses:",
              subpoints: [
                "• Post accurate job listings.",
                "• Comply with labour laws of your country."
              ]
            },
            {
              subtitle: "Job Seekers:",
              subpoints: [
                "• Provide truthful information.",
                "• Understand that submitting an application does not guarantee placement."
              ]
            }
          ]
        },
        {
          subtitle: "3. Accuracy & Ownership:",
          subpoints: [
            "• Producer Buddy is not responsible for job listing accuracy.",
            "• Applicants are responsible for their resumes and application materials."
          ]
        },
        {
          subtitle: "4. Data Usage:",
          content: "Data is processed as per our Privacy Policy. Users consent to essential data sharing during recruitment."
        },
        {
          subtitle: "5. Job Posting Rules:",
          subpoints: [
            "• No offensive, misleading, or discriminatory content.",
            "• No duplicate or fraudulent listings."
          ]
        },
        {
          subtitle: "6. Application Process:",
          subpoints: [
            "• Applications must be submitted via the platform.",
            "• Producer Buddy facilitates the process but is not involved in hiring decisions."
          ]
        },
        {
          subtitle: "7. Disclaimer:",
          subpoints: [
            "• We do not guarantee job offers or the legitimacy of all listings.",
            "• External job links are used at your own risk."
          ]
        },
        {
          subtitle: "8. Prohibited Activities:",
          content: "No use outside the scope of hiring in the film and entertainment industry. No security breaches or fraud."
        },
        {
          subtitle: "9. Indemnification:",
          content: "Users indemnify Producer Buddy against legal issues arising from misuse of the Career Page."
        },
        {
          subtitle: "10. Termination:",
          content: "We may remove content or block access for violations of these terms."
        }
      ]
    },
    {
      title: "B. Terms for Distributor Connection Feature",
      subpoints: [
        {
          subtitle: "1. Acceptance:",
          content: "Use of the distributor feature implies consent to these terms."
        },
        {
          subtitle: "2. Use Guidelines:",
          subcategories: [
            {
              subtitle: "Producers:",
              subpoints: [
                "• Provide truthful project information.",
                "• Adhere to copyright and legal standards."
              ]
            },
            {
              subtitle: "Distributors:",
              subpoints: [
                "• Share valid credentials and professional terms.",
                "• Maintain appropriate communication."
              ]
            }
          ]
        },
        {
          subtitle: "3. Platform Role:",
          content: "Producer Buddy acts solely as a facilitator and does not:",
          subpoints: [
            "• Guarantee partnerships.",
            "• Participate in contracts or payments."
          ]
        },
        {
          subtitle: "4. Content Responsibility:",
          content: "Users must ensure the accuracy of uploaded content and avoid copyright violations."
        },
        {
          subtitle: "5. Privacy:",
          content: "Shared data may be visible to parties involved. Refer to the Privacy Policy for details."
        },
        {
          subtitle: "6. Prohibited Activities:",
          subcategories: [
            {
              subtitle: "Ownership:",
              content: "Posters, trailers, and materials remain the property of the producers. Unauthorized redistribution is strictly forbidden."
            },
            {
              subtitle: "Platform Misuse:",
              subpoints: [
                "• No misleading, irrelevant, or abusive content.",
                "• No use beyond production and distribution purposes."
              ]
            },
            {
              subtitle: "Confidentiality & Legal Compliance:",
              content: "Any unauthorized sharing or misuse may lead to legal consequences under:",
              subpoints: [
                "• Indian Contract Act, 1872",
                "• IT Act, 2000 (Amended 2008)",
                "• Copyright Act, 1957"
              ]
            }
          ]
        }
      ]
    },
    {
      title: "C. Terms for Location Scouting",
      subpoints: [
        {
          subtitle: "1. Third-Party Listings:",
          subpoints: [
            "• The platform may feature third-party locations. Producer Buddy is not responsible for the accuracy, availability, or suitability of these listings.",
            "• Users must independently verify location details and negotiate terms directly with the property owners or managers."
          ]
        },
        {
          subtitle: "2. Usage Restrictions:",
          subpoints: [
            "• Users are prohibited from sharing location details or images obtained through the platform without prior consent from the respective property owners.",
            "• The platform must not be used for unauthorized or illegal location scouting activities."
          ]
        },
        {
          subtitle: "3. Liability Disclaimer:",
          subpoints: [
            "• Producer Buddy does not guarantee the successful acquisition or use of any location.",
            "• Users assume all responsibility for any agreements or issues arising from location scouting activities."
          ]
        }
      ]
    }
  ];
  
  const chatbotTermsSections = [
    {
      title: "1. AI-Powered Responses",
      content: "The Producer Buddy Chatbot uses artificial intelligence (AI) and machine learning models to provide automated responses.",
      subpoints: [
        "• While we strive to deliver accurate and helpful information, responses may occasionally contain errors, inaccuracies, or omissions due to the nature of AI.",
        "• All responses are intended for general informational purposes and are not a substitute for professional advice or judgment.",
        "• The chatbot delivers responses with an approximate accuracy rate of 99%, but accuracy also depends on the quality and clarity of the user's query."
      ]
    },
    {
      title: "2. Limitation of Liability",
      subpoints: [
        "• Producer Buddy shall not be held liable for any actions, decisions, or consequences resulting from the use of its chatbot.",
        "• Users assume full responsibility for how they use and interpret the chatbot's responses.",
        "• On a positive note, the chatbot is designed to empower users to make informed choices and decisions.",
        "• Users are encouraged to take ownership of their actions based on the guidance provided."
      ]
    },
    {
      title: "3. Data Privacy and Usage",
      content: "Your interactions with the Producer Buddy Chatbot are recorded to help improve functionality and user experience.",
      subpoints: [
        "• Data Collection: We may log information such as chat content, name, and email address.",
        "• Purpose: Data is used solely for enhancing the chatbot experience and for internal marketing insights. It will not be used to train AI models without explicit consent.",
        "• Data Handling: All data is handled in accordance with applicable laws and the Producer Buddy Privacy Policy(link).",
        "• Data Deletion: To request deletion of your chatbot interaction history, please contact us at support@producersbuddy.com."
      ]
    },
    {
      title: "4. Acknowledgment of AI Limitations",
      subpoints: [
        "• The chatbot may occasionally generate responses based on incomplete or unclear input.",
        "• For best results, we encourage users to ask clear and detailed questions to improve the accuracy and relevance of responses."
      ]
    },
    {
      title: "5. Feedback and Support",
      content: "Your feedback matters to us!",
      subpoints: [
        "• To share your experience or suggestions, please [click here to provide feedback](#).",
        "• For questions or support regarding the chatbot, please contact us at support@producersbuddy.com."
      ]
    },
    {
      title: "6. Acceptance of Terms",
      content: "By using the Producer Buddy Chatbot, you agree to these Terms of Use and Disclaimer. If you do not agree with these terms, please refrain from using the chatbot."
    }
  ];

  const renderSubpoints = (subpoints) => {
    return (
      <ul className="list-none pl-5 mt-2 space-y-2">
        {subpoints.map((point, idx) => (
          <li key={idx} className="text-gray-700">{point}</li>
        ))}
      </ul>
    );
  };

  const renderSection = (section) => {
    return (
      <div className="pb-6 border-b border-gray-200 last:border-b-0">
        <h2 className="text-xl font-semibold mb-3 text-gray-900">{section.title}</h2>
        
        {section.content && <p className="text-gray-700 leading-relaxed mb-2">{section.content}</p>}
        
        {section.subtitle && <h3 className="text-lg font-medium mt-3 mb-2 text-gray-800">{section.subtitle}</h3>}
        
        {section.subpoints && renderSubpoints(section.subpoints)}
        
        {section.additional && <p className="text-gray-700 leading-relaxed mt-3">{section.additional}</p>}
        
        {section.subcategories && (
          <div className="mt-3 space-y-4">
            {section.subcategories.map((subcat, idx) => (
              <div key={idx}>
                {subcat.subtitle && <h4 className="text-base font-medium mb-2 text-gray-800">{subcat.subtitle}</h4>}
                {subcat.content && <p className="text-gray-700 mb-2">{subcat.content}</p>}
                {subcat.subpoints && renderSubpoints(subcat.subpoints)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSupplementalSection = (section) => {
    return (
      <div className="pb-6 border-b border-gray-200 last:border-b-0">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">{section.title}</h2>
        
        {section.content && <p className="text-gray-700 leading-relaxed mb-3">{section.content}</p>}
        
        {section.subpoints && (
          <div className="space-y-5 mt-2">
            {section.subpoints.map((subpoint, idx) => (
              <div key={idx}>
                {subpoint.subtitle && <h3 className="text-lg font-medium mb-2 text-gray-800">{subpoint.subtitle}</h3>}
                {subpoint.content && <p className="text-gray-700 mb-2">{subpoint.content}</p>}
                {subpoint.subpoints && renderSubpoints(subpoint.subpoints)}
                
                {subpoint.subcategories && (
                  <div className="mt-3 ml-4 space-y-4">
                    {subpoint.subcategories.map((subcat, scidx) => (
                      <div key={scidx} className="mb-3">
                        {subcat.subtitle && <h4 className="text-base font-medium mb-2 text-gray-800">{subcat.subtitle}</h4>}
                        {subcat.content && <p className="text-gray-700 mb-2">{subcat.content}</p>}
                        {subcat.subpoints && renderSubpoints(subcat.subpoints)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <SEO
      title="Terms and Conditions"
      description="Read ProducerBuddy's terms and conditions governing the use of our film production platform and services."
      url="https://producerbuddy.pilomation.com/terms"
    />
    <Nav />
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('terms')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'terms'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Terms and Conditions
            </button>
            <button
              onClick={() => setActiveTab('chatbot')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'chatbot'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Chatbot Terms of Use
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'terms' && (
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-gray-900">Terms and Conditions</h1>
              <p className="mt-2 text-gray-600">Last updated: April 11, 2025</p>
            </div>

            <div className="prose max-w-none">
              <p className="text-lg mb-8">
                Welcome to Producer Buddy, a product of Pilomation. By using our platform, you agree to abide by the following terms and conditions. Please read them carefully.
              </p>

              <div className="space-y-8">
                {termsAndConditionsSections.map((section, index) => (
                  <div key={index}>
                    {renderSection(section)}
                  </div>
                ))}
                
                <h1 className="text-2xl font-bold mt-12 mb-6 text-gray-900">Supplemental Terms</h1>
                
                {supplementalTerms.map((section, index) => (
                  <div key={`supplemental-${index}`}>
                    {renderSupplementalSection(section)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chatbot' && (
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-gray-900">Chatbot Terms of Use and Disclaimer</h1>
              <p className="mt-2 text-gray-600">Last updated: April 11, 2025</p>
            </div>

            <div className="prose max-w-none">
              <p className="text-lg mb-8">
                Welcome to the Producer Buddy Chatbot — your AI-powered assistant designed to enhance your experience with the Producer Buddy platform. Please read these Terms of Use and Disclaimer carefully before using the chatbot. By interacting with the chatbot, you acknowledge and agree to the following terms:
              </p>

              <div className="space-y-8">
                {chatbotTermsSections.map((section, index) => (
                  <div key={index}>
                    {renderSection(section)}
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center text-gray-700">
                <p>Thank you for using the Producer Buddy Chatbot. We're here to support your journey in revolutionizing film production!</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
    </>
  );
}
